/* The organizer's OWN site navigation — public microsites never show AOS chrome.
   Brand (logo or wordmark) + configured section menu + language selector +
   subtle share. Also rendered (non-interactive) at the top of the builder
   canvas so a freshly uploaded logo is visible immediately. */
import { ArrowLeft, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { openActivityCenter } from "@/lib/activityCenter";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { ShareMenu } from "@/components/ShareMenu";
import { BACKEND_URL, mediaUrlFromMap } from "@/lib/api";
import { useHostTenant } from "@/lib/hostTenant";

export const MicrositeNav = ({
  organizer,
  media,
  theme,
  sections = [],
  onSectionClick,
  backTo,
  shareTitle,
  registerTo = null, // Fase A: CTA "Register to bid" para visitantes no autenticados
  preview = false,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logoId = theme?.logo_media_id || organizer?.theme?.logo_media_id || organizer?.logo_media_id;
  const logoUrl =
    (logoId && (mediaUrlFromMap(media, logoId) || (preview ? `${BACKEND_URL}/api/media/file/${logoId}` : null))) || null;
  // C3: organizer-adjustable logo height (clamped so the h-14 bar never breaks)
  const logoHeight = Math.min(theme?.logo_height || organizer?.theme?.logo_height || 32, 48);
  const tenant = useHostTenant();
  const brandTo = tenant && organizer?.slug === tenant.slug ? "/" : organizer?.slug ? `/${organizer.slug}` : null;

  const brand = logoUrl ? (
    <img
      src={logoUrl}
      alt={organizer?.name || ""}
      data-testid="microsite-nav-logo"
      className="w-auto max-w-[160px] object-contain"
      style={{ height: `${logoHeight}px` }}
    />
  ) : (
    <span
      data-testid="microsite-nav-name"
      className="text-sm font-semibold tracking-[0.12em] uppercase text-[#0A0A0A] truncate"
    >
      {organizer?.name}
    </span>
  );

  const sectionLinks = (extra = "") =>
    sections.map((s) => (
      <a
        key={s.id}
        href={`#sec-${s.type}`}
        onClick={(e) => onSectionClick?.(e, s.type)}
        data-testid={`microsite-nav-section-${s.type}`}
        className={`text-xs whitespace-nowrap text-black/60 hover:text-black transition-colors duration-200 ${extra}`}
      >
        {s.label}
      </a>
    ));

  return (
    <header
      data-testid="microsite-nav"
      className={`${preview ? "" : "sticky top-0"} z-40 bg-white/85 backdrop-blur-xl border-b border-black/5`}
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-12 h-14 flex items-center gap-5">
        {brandTo && !preview ? (
          <Link to={brandTo} data-testid="microsite-nav-brand" className="flex items-center min-w-0 shrink-0 hover:opacity-75 transition-opacity duration-200">
            {brand}
          </Link>
        ) : (
          <span data-testid="microsite-nav-brand" className="flex items-center min-w-0 shrink-0">
            {brand}
          </span>
        )}

        {backTo && !preview && (
          <>
            <span className="w-px h-4 bg-black/10 shrink-0 hidden sm:block" aria-hidden="true" />
            <Link
              to={backTo.to}
              data-testid="microsite-nav-back"
              className="inline-flex items-center gap-1.5 text-xs text-black/60 hover:text-black transition-colors duration-200 min-w-0"
            >
              <ArrowLeft className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span className="truncate max-w-[220px]">{backTo.label}</span>
            </Link>
          </>
        )}

        {/* Phase 6: the auction belongs to the organizer WEBSITE — permanent links home */}
        {brandTo && !preview && (
          <div className="hidden md:flex items-center gap-5 shrink-0">
            <span className="w-px h-4 bg-black/10" aria-hidden="true" />
            <Link to={brandTo} data-testid="microsite-nav-website-home" className="text-xs whitespace-nowrap text-black/60 hover:text-black transition-colors duration-200">
              {t("web.home")}
            </Link>
            <Link to={`${brandTo}#auctions`} data-testid="microsite-nav-website-auctions" className="text-xs whitespace-nowrap text-black/60 hover:text-black transition-colors duration-200">
              {t("web.auctions")}
            </Link>
          </div>
        )}

        <nav className="ml-auto hidden sm:flex items-center gap-6 overflow-x-auto [scrollbar-width:none]" aria-label={organizer?.name || ""}>
          {sectionLinks()}
        </nav>

        <div className={`flex items-center gap-0.5 shrink-0 ${sections.length ? "" : "ml-auto sm:ml-0"} ${sections.length ? "" : "sm:ml-auto"}`}>
          <LanguageSwitcher />
          {/* C4: asistente junto al selector de idioma para usuarios logueados
              (el flotante desaparece en sesión y aquí no hay otra barra fija) */}
          {user && !preview && (
            <button
              type="button"
              onClick={openActivityCenter}
              aria-label={t("ai.centerTitle")}
              title={t("ai.centerTitle")}
              data-testid="microsite-nav-assistant"
              className="inline-flex items-center justify-center h-8 w-8 rounded-full text-black/60 hover:text-black hover:bg-black/5 transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" />
            </button>
          )}
          {shareTitle && !preview && <ShareMenu title={shareTitle} variant="icon" testid="microsite-nav-share" />}
          {/* Fase A: el visitante de una subasta es un COMPRADOR — registro
              white-label dentro del contexto, jamás el signup de organizadores */}
          {registerTo && !user && !preview && (
            <Link
              to={registerTo}
              data-testid="microsite-nav-register"
              className="ml-1.5 inline-flex items-center h-8 px-4 rounded-full text-xs font-semibold text-white whitespace-nowrap transition-opacity duration-200 hover:opacity-85"
              style={{ backgroundColor: "var(--brand-button, #0A0A0A)" }}
            >
              {t("reg.cta")}
            </Link>
          )}
        </div>
      </div>

      {sections.length >= 2 && (
        <div className="sm:hidden border-t border-black/5 px-6 flex items-center gap-6 overflow-x-auto h-10 [scrollbar-width:none]">
          {sectionLinks()}
        </div>
      )}
    </header>
  );
};
